import React from "react";
import { PaginatedResult } from "@/types/breeze/Any";
import { SkeletonTableRows } from "@/components/ui/Skeleton";
import {
  Box,
  Table,
  Text,
  Flex,
  Button,
  ScrollArea,
} from "@radix-ui/themes";

export interface Column<T> {
  header: string;
  accessor?: keyof T | undefined;
  maxWidth?: number;
  cellRenderer?: (row: T) => React.ReactNode;
}

interface BasicTableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  rowClassName?: string;
  isLoading?: boolean;
  pagination?: PaginatedResult<T>;
  onPageChange?: (page: number) => void;
  onlyColumns?: string[];
  entityName?: string;
}

export function BasicTable<T>({
  columns,
  data,
  onRowClick,
  rowClassName,
  isLoading = false,
  pagination,
  onPageChange,
  onlyColumns = [],
  entityName = "",
}: BasicTableProps<T>) {
  // Filter and sort columns
  const orderedColumns = React.useMemo(() => {
    // First, filter out tenant_id
    const filteredColumns = columns.filter(
      (col) =>
        col.accessor !== "tenant_id" &&
        col.header.toLowerCase() !== "tenant id" &&
        col.accessor !== "locked" &&
        col.header.toLowerCase() !== "locked" &&
        isInIncludedColumns<T>(entityName, onlyColumns, col),
    );

    // Separate timestamp columns and action columns
    const timestampColumns = filteredColumns.filter((col) => {
      const header = col.header.toLowerCase();
      const accessor = col.accessor ? String(col.accessor).toLowerCase() : "";
      return (
        header === "created at" ||
        header === "updated at" ||
        accessor === "created_at" ||
        accessor === "updated_at"
      );
    });

    const actionColumns = filteredColumns.filter((col) => {
      const header = col.header.toLowerCase();
      return header === "actions" || header === "action";
    });

    const regularColumns = filteredColumns.filter((col) => {
      return !timestampColumns.includes(col) && !actionColumns.includes(col);
    });

    // Combine the columns in the desired order
    return [...regularColumns, ...timestampColumns, ...actionColumns];
  }, [columns]);

  const renderCell = (row: T, column: Column<T>): React.ReactNode => {
    if (column.cellRenderer) {
      return column.cellRenderer(row);
    }
    if (column.accessor) {
      const value = row[column.accessor];

      // Handle null/undefined
      if (value === null || value === undefined) {
        return "N/A";
      }

      // Handle booleans
      if (typeof value === "boolean") {
        return value ? "Yes" : "No";
      }

      // Handle arrays
      if (value instanceof Array) {
        return `${value.length} items`;
      }

      // Handle dates - check both Date objects and ISO strings
      if (value instanceof Date) {
        // If the field name contains these keywords, use appropriate formatter
        const fieldName = String(column.accessor).toLowerCase();
        if (fieldName.includes("timestamp") || fieldName.includes("_at")) {
          return value.toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "medium",
          });
        }
        return value.toLocaleDateString();
      }

      // Only try to parse strings that look like dates (ISO format)
      if (
        typeof value === "string" &&
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value) &&
        !isNaN(Date.parse(value))
      ) {
        const date = new Date(value);
        const fieldName = String(column.accessor).toLowerCase();
        if (fieldName.includes("timestamp") || fieldName.includes("_at")) {
          return date.toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "medium",
          });
        }
        return date.toLocaleDateString();
      }

      return String(value);
    }
    return "Column Configuration Error";
  };

  const handleRowClick = (row: T) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  return (
    <div>
      {pagination && (
        <Box
          position="sticky"
          top="0"
          pb="3"
          style={{ borderBottom: "1px solid var(--gray-a5)" }}
        >
          <Flex justify="between" align="center" p="3">
            <Text size="2">
              Showing{" "}
              <Text weight="medium" as="span">
                {(pagination.page - 1) * pagination.page_size + 1}
              </Text>{" "}
              to{" "}
              <Text weight="medium" as="span">
                {Math.min(
                  pagination.page * pagination.page_size,
                  pagination.total_items,
                )}
              </Text>{" "}
              of{" "}
              <Text weight="medium" as="span">
                {pagination.total_items}
              </Text>{" "}
              results
            </Text>
            <Flex gap="2" align="center">
              <Button
                variant="soft"
                color="gray"
                size="1"
                onClick={() => onPageChange?.(pagination.page - 1)}
                disabled={!pagination.has_previous_page}
              >
                Previous
              </Button>
              <Text size="2">
                Page {pagination.page} of {pagination.total_pages}
              </Text>
              <Button
                variant="soft"
                color="gray"
                size="1"
                onClick={() => onPageChange?.(pagination.page + 1)}
                disabled={!pagination.has_next_page}
              >
                Next
              </Button>
            </Flex>
          </Flex>
        </Box>
      )}

      <ScrollArea>
        <Table.Root>
          <Table.Header>
            <Table.Row>
              {!isLoading &&
                data.length !== 0 &&
                orderedColumns.map((column, index) => (
                  <Table.ColumnHeaderCell
                    key={index}
                    style={{
                      ...(column.maxWidth
                        ? {
                            maxWidth: `${column.maxWidth}px`,
                            width: `${column.maxWidth}px`,
                          }
                        : {}),
                      backgroundColor: "var(--gray-a2)",
                      fontWeight: "600",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      verticalAlign: "middle",
                    }}
                  >
                    {column.header}
                  </Table.ColumnHeaderCell>
                ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isLoading ? (
              <Table.Row>
                <Table.Cell colSpan={orderedColumns.length}>
                  <SkeletonTableRows
                    rows={5}
                    columns={orderedColumns.length || 4}
                  />
                </Table.Cell>
              </Table.Row>
            ) : data.length === 0 ? (
              <Table.Row>
                <Table.Cell
                  colSpan={orderedColumns.length}
                  align="center"
                  style={{ height: "150px" }}
                >
                  <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    gap="2"
                    style={{ height: "100%" }}
                  >
                    <Text size="2" color="gray" weight="medium">
                      No data available
                    </Text>
                    <Text size="1" color="gray">
                      Try adjusting your filters or adding new records
                    </Text>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ) : (
              data.map((row, rowIndex) => (
                <Table.Row
                  key={rowIndex}
                  style={{
                    cursor: onRowClick ? "pointer" : "default",
                    transition: "background-color 0.2s",
                    ...(!isLoading && {
                      "&:hover": {
                        backgroundColor: "var(--gray-a2)",
                      },
                    }),
                  }}
                  className={rowClassName || ""}
                  onClick={onRowClick ? () => handleRowClick(row) : undefined}
                >
                  {orderedColumns.map((column, colIndex) => (
                    <Table.Cell
                      key={colIndex}
                      style={{
                        ...(column.maxWidth
                          ? {
                              maxWidth: `${column.maxWidth}px`,
                              width: `${column.maxWidth}px`,
                            }
                          : {}),
                        padding: "var(--space-3)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        borderBottom: "1px solid var(--gray-a3)",
                        verticalAlign: "middle",
                      }}
                    >
                      <Text size="2">{renderCell(row, column)}</Text>
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </ScrollArea>
    </div>
  );
}

function isInIncludedColumns<T>(
  entityName: string,
  onlyColumns: string[],
  col: Column<T>,
): unknown {
  if (entityName != null && onlyColumns.length) {
    return onlyColumns
      .concat([
        prefixedColumnName(entityName, "created_at"),
        prefixedColumnName(entityName, "updated_at"),
        prefixedColumnName(entityName, "actions"),
      ])
      .includes(
        prefixedColumnName(
          entityName,
          col.accessor ? col.accessor.toString() : col.header,
        ),
      );
  } else {
    return true;
  }
}

function prefixedColumnName(entityName: string, columnName: string) {
  return ((entityName ?? "") + "_" + columnName).toLowerCase();
}
